import { db } from './db';
import type { Repository } from './repository';

export class LocalRepository implements Repository {
  async getFoyer(): Promise<unknown | null> {
    const all = await db.foyers.toArray();
    return all[0] ?? null;
  }

  async saveFoyer(foyer: unknown): Promise<void> {
    if ((foyer as { createdBy?: unknown }).createdBy === undefined) {
      throw new Error('saveFoyer: createdBy is undefined — use createFoyer() factory');
    }
    await db.foyers.put(foyer);
  }

  async getLines(foyerId: string): Promise<unknown[]> {
    return db.lines.where('foyerId').equals(foyerId).toArray();
  }

  async saveLine(line: unknown): Promise<void> {
    await db.lines.put(line);
  }

  async deleteLine(id: string): Promise<void> {
    await db.lines.delete(id);
  }

  async saveLines(lines: unknown[]): Promise<void> {
    await db.lines.bulkPut(lines);
  }

  async getDomains(foyerId: string): Promise<unknown[]> {
    return db.domains.where('foyerId').equals(foyerId).toArray();
  }

  async saveDomain(domain: unknown): Promise<void> {
    await db.domains.put(domain);
  }

  async deleteDomain(id: string): Promise<void> {
    await db.domains.delete(id);
  }

  async getCategories(foyerId: string): Promise<unknown[]> {
    return db.categories.where('foyerId').equals(foyerId).toArray();
  }

  async saveCategory(category: unknown): Promise<void> {
    await db.categories.put(category);
  }

  async deleteCategory(id: string): Promise<void> {
    await db.categories.delete(id);
  }

  async getBlocks(foyerId: string): Promise<unknown[]> {
    return db.blocks.where('foyerId').equals(foyerId).toArray();
  }

  async saveBlock(block: unknown): Promise<void> {
    await db.blocks.put(block);
  }

  async getBudgetSnapshots(foyerId: string): Promise<unknown[]> {
    return db.budgetSnapshots.where('foyerId').equals(foyerId).toArray();
  }

  async createBudgetSnapshot(snapshot: unknown): Promise<void> {
    await db.budgetSnapshots.put(snapshot);
  }

  async getScenarios(foyerId: string): Promise<unknown[]> {
    return db.scenarios.where('foyerId').equals(foyerId).toArray();
  }

  async saveScenario(scenario: unknown): Promise<void> {
    if ((scenario as { createdBy?: unknown }).createdBy === undefined) {
      throw new Error('saveScenario: createdBy is undefined — use createScenario() factory');
    }
    await db.scenarios.put(scenario);
  }

  async deleteScenario(id: string): Promise<void> {
    await db.scenarios.delete(id);
  }

  async getObjectives(foyerId: string): Promise<unknown[]> {
    return db.objectives.where('foyerId').equals(foyerId).toArray();
  }

  async saveObjective(obj: unknown): Promise<void> {
    if ((obj as { createdBy?: unknown }).createdBy === undefined) {
      throw new Error('saveObjective: createdBy is undefined — use createObjective() factory');
    }
    await db.objectives.put(obj);
  }

  async deleteObjective(id: string): Promise<void> {
    await db.objectives.delete(id);
  }

  async getPatrimoineLines(foyerId: string): Promise<unknown[]> {
    return db.patrimoineLines.where('foyerId').equals(foyerId).toArray();
  }

  async savePatrimoineLine(line: unknown): Promise<void> {
    await db.patrimoineLines.put(line);
  }

  async deletePatrimoineLine(id: string): Promise<void> {
    await db.patrimoineLines.delete(id);
  }

  async getPatrimoineCategories(foyerId: string): Promise<unknown[]> {
    return db.patrimoineCategories.where('foyerId').equals(foyerId).toArray();
  }

  async savePatrimoineCategory(cat: unknown): Promise<void> {
    await db.patrimoineCategories.put(cat);
  }

  async deletePatrimoineCategory(id: string): Promise<void> {
    await db.patrimoineCategories.delete(id);
  }

  async getPatrimoineSnapshots(foyerId: string): Promise<unknown[]> {
    return db.patrimoineSnapshots.where('foyerId').equals(foyerId).toArray();
  }

  async createPatrimoineSnapshot(snapshot: unknown): Promise<void> {
    await db.patrimoineSnapshots.put(snapshot);
  }

  async deletePatrimoineSnapshot(id: string): Promise<void> {
    await db.patrimoineSnapshots.delete(id);
  }

  async getKpiThresholds(foyerId: string): Promise<unknown | null> {
    return (await db.kpiThresholds.get(foyerId)) ?? null;
  }

  async saveKpiThresholds(thresholds: unknown): Promise<void> {
    await db.kpiThresholds.put(thresholds);
  }

  async acquireLock(): Promise<{ acquired: boolean }> {
    return { acquired: true };
  }

  async releaseLock(): Promise<void> {}

  async heartbeat(): Promise<void> {}

  async getLockStatus(): Promise<{ locked: boolean; lockedBy?: string }> {
    return { locked: false };
  }

  async exportAll(): Promise<unknown> {
    const [foyers, blocks, domains, categories, lines, objectives, scenarios, budgetSnapshots, patrimoineLines, patrimoineCategories, patrimoineSnapshots, kpiThresholds] = await Promise.all([
      db.foyers.toArray(),
      db.blocks.toArray(),
      db.domains.toArray(),
      db.categories.toArray(),
      db.lines.toArray(),
      db.objectives.toArray(),
      db.scenarios.toArray(),
      db.budgetSnapshots.toArray(),
      db.patrimoineLines.toArray(),
      db.patrimoineCategories.toArray(),
      db.patrimoineSnapshots.toArray(),
      db.kpiThresholds.toArray(),
    ]);
    return { foyers, blocks, domains, categories, lines, objectives, scenarios, budgetSnapshots, patrimoineLines, patrimoineCategories, patrimoineSnapshots, kpiThresholds };
  }

  async importAll(data: unknown): Promise<void> {
    const d = data as Record<string, unknown[]>;
    await db.transaction('rw', db.tables, async () => {
      for (const table of db.tables) {
        await table.clear();
        const rows = d[table.name];
        if (rows?.length) await table.bulkPut(rows);
      }
    });
  }

  async deleteAll(): Promise<void> {
    await db.transaction('rw', db.tables, async () => {
      for (const table of db.tables) {
        await table.clear();
      }
    });
  }
}
